// Mock data for the Critical Thinking App

export const cognitiveBiases = [
  {
    id: 1,
    name: "Confirmation Bias",
    shortDescription: "The art of cherry-picking reality",
    satiricalQuote: "Why seek truth when you can just confirm what you already 'know'?",
    description: "The tendency to search for, interpret, and recall information that confirms our pre-existing beliefs. Social media algorithms have turned this into an Olympic sport.",
    modernExample: "Scrolling through your feed until you find that one article that proves your political opinion is absolutely correct, while ignoring the 47 studies that suggest otherwise.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Francis Bacon (1620)",
    funFact: "Your brain literally rewards you with dopamine for confirming your biases. Evolution's cruelest joke.",
    interactionCount: 1247,
    realWorldImpact: "From stock market bubbles to political polarisation, this bias shapes entire societies."
  },
  {
    id: 2,
    name: "Dunning-Kruger Effect",
    shortDescription: "Peak confidence at minimum competence",
    satiricalQuote: "A little knowledge is a dangerous thing, but a lot of confidence in that little knowledge is catastrophic.",
    description: "People with low ability at a task overestimate their own ability. The internet comment section's patron saint.",
    modernExample: "Reading one Wikipedia article about vaccines and suddenly feeling qualified to debate epidemiologists on Twitter.",
    severity: "High", 
    category: "Self-Assessment",
    algorithmicAmplification: "High",
    discoveredBy: "David Dunning & Justin Kruger (1999)",
    funFact: "The people most affected by this bias are the least likely to recognise they have it. Meta-irony at its finest.",
    interactionCount: 892,
    realWorldImpact: "Leads to dangerous overconfidence in critical decisions and spreads misinformation online."
  },
  {
    id: 3,
    name: "Aesthetic Bias",
    shortDescription: "What looks good must be good",
    satiricalQuote: "Beauty isn't just skin deep—it's a full cognitive override",
    description: "The tendency to favour aesthetically pleasing options over more effective or efficient ones. We judge books by their covers and assume beautiful interfaces work better.",
    modernExample: "Choosing a sleek but impractical app over a functional but plain one, or trusting attractive people more in professional settings despite equal qualifications.",
    severity: "Medium",
    category: "Perception & Judgement",
    algorithmicAmplification: "High",
    discoveredBy: "Documented across cognitive psychology research (multiple researchers)",
    funFact: "The 'aesthetic-usability effect' shows we perceive beautiful designs as more user-friendly, even when they're not.",
    interactionCount: 654,
    realWorldImpact: "Influences hiring decisions, product choices, and trust in information based on presentation rather than substance."
  },
  {
    id: 4,
    name: "Bandwagon Effect",
    shortDescription: "Monkey see, monkey do... but make it intellectual",
    satiricalQuote: "Individual thinking is so last century. Why not let the crowd decide for you?",
    description: "The tendency to adopt beliefs or behaviours because many others have done so. Democracy's dark side and social media's fuel.",
    modernExample: "Buying GameStop stock because 'everyone on Reddit is doing it' without understanding what GameStop actually does.",
    severity: "Medium",
    category: "Social Influence",
    algorithmicAmplification: "Extreme",
    discoveredBy: "Social psychologists (1950s)",
    funFact: "The term comes from politicians literally jumping on bandwagons during parades to show they were 'with the people.'",
    interactionCount: 1134,
    realWorldImpact: "Creates market bubbles, viral misinformation, and can override individual critical thinking entirely."
  },
  {
    id: 5,
    name: "Sunk Cost Fallacy",
    shortDescription: "Throwing good money after bad, but philosophically",
    satiricalQuote: "I've come too far to quit now... even though 'too far' is actually nowhere.",
    description: "Continuing a course of action because of previously invested resources rather than future value. Netflix's watch history knows your pain.",
    modernExample: "Watching 3 more seasons of a terrible show because you've 'already invested so much time in the characters.'",
    severity: "Medium",
    category: "Decision Making",
    algorithmicAmplification: "Medium",
    discoveredBy: "Richard Thaler (1980)",
    funFact: "Casinos literally design their environments to exploit this bias. Those chips aren't just plastic—they're sunk cost generators.",
    interactionCount: 743,
    realWorldImpact: "Keeps people in bad relationships, failing businesses, and endless Netflix binges."
  },
  {
    id: 6,
    name: "Anchoring Bias",
    shortDescription: "The first number you hear becomes your reality",
    satiricalQuote: "Why think when the first piece of information can do all the work for you?",
    description: "The tendency to rely too heavily on the first piece of information encountered when making decisions. Retailers have weaponised this into an art form.",
    modernExample: "Seeing a $200 'original price' crossed out next to a $100 'sale price' and thinking you got a great deal, when the item was never actually sold for $200.",
    severity: "High",
    category: "Decision Making",
    algorithmicAmplification: "Very High",
    discoveredBy: "Amos Tversky & Daniel Kahneman (1974)",
    funFact: "Even random numbers can anchor your decisions. People shown higher random numbers give higher estimates for completely unrelated questions.",
    interactionCount: 934,
    realWorldImpact: "Influences salary negotiations, real estate prices, and consumer purchasing decisions worldwide."
  },
  {
    id: 7,
    name: "Hindsight Bias",
    shortDescription: "I knew it all along... or did I?",
    satiricalQuote: "Predicting the past is surprisingly easy when you already know what happened.",
    description: "The tendency to perceive past events as more predictable than they were. Also known as the 'I-knew-it-all-along' effect.",
    modernExample: "After a stock market crash, claiming you 'saw it coming all along' despite never selling your stocks or warning anyone beforehand.",
    severity: "Medium",
    category: "Memory",
    algorithmicAmplification: "Medium",
    discoveredBy: "Baruch Fischhoff (1975)",
    funFact: "This bias makes us terrible at learning from history because we think past events were more obvious than they actually were.",
    interactionCount: 687,
    realWorldImpact: "Prevents proper learning from mistakes and creates overconfidence in future predictions."
  },
  {
    id: 8,
    name: "Self-Serving Bias",
    shortDescription: "I'm successful because I'm great; I failed because of circumstances",
    satiricalQuote: "Success is obviously due to my brilliance. Failure? That's just bad luck.",
    description: "The tendency to attribute successes to internal factors and failures to external factors. Your ego's personal PR department.",
    modernExample: "Getting promoted because you're 'naturally talented' but failing a project because the 'team wasn't supportive enough.'",
    severity: "High",
    category: "Attribution",
    algorithmicAmplification: "High",
    discoveredBy: "Dale Miller & Michael Ross (1975)",
    funFact: "This bias is so strong that even when people are told about it, they still think it doesn't apply to them.",
    interactionCount: 1156,
    realWorldImpact: "Prevents learning from failures and creates unrealistic self-assessments in everything from dating to business."
  },
  {
    id: 9,
    name: "Optimism Bias",
    shortDescription: "Everything will be fine... probably",
    satiricalQuote: "Bad things happen to other people. Good things happen to me. Statistics are just suggestions.",
    description: "The tendency to overestimate positive outcomes and underestimate risks. The reason startup founders exist.",
    modernExample: "Thinking you'll definitely finish that project early while simultaneously running 30 minutes late to this meeting.",
    severity: "Medium",
    category: "Probability Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Neil Weinstein (1980)",
    funFact: "Optimism bias is actually linked to better mental health, but worse financial planning. Choose your trade-offs wisely.",
    interactionCount: 892,
    realWorldImpact: "Leads to inadequate preparation, poor risk assessment, and the occasional pleasant surprise when reality exceeds expectations."
  },
  {
    id: 10,
    name: "Negativity Bias",
    shortDescription: "Bad news travels faster than good news in your brain",
    satiricalQuote: "One criticism cancels out ten compliments. Math has never been more depressing.",
    description: "The tendency to give more weight to negative information than positive. Evolution's anxiety disorder.",
    modernExample: "Remembering every mean comment on your social media post while completely forgetting the 50 likes and positive responses.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Paul Rozin & Edward Royzman (2001)",
    funFact: "Your brain processes negative information about 5 times faster than positive information. Thanks, evolution.",
    interactionCount: 1389,
    realWorldImpact: "Drives news media algorithms, creates anxiety disorders, and explains why customer complaints get more attention than praise."
  },
  {
    id: 11,
    name: "Fundamental Attribution Error",
    shortDescription: "They're late because they're lazy; I'm late because of traffic",
    satiricalQuote: "When I mess up, it's the situation. When you mess up, it's your character. Simple.",
    description: "The tendency to overemphasise dispositional factors over situational factors in explaining others' behaviour.",
    modernExample: "Assuming the slow cashier is incompetent rather than considering they might be new, dealing with a difficult system, or having a bad day.",
    severity: "High",
    category: "Attribution",
    algorithmicAmplification: "High",
    discoveredBy: "Lee Ross (1977)",
    funFact: "This bias is stronger in individualistic cultures and weaker in collectivistic cultures. Even our biases have cultural preferences.",
    interactionCount: 1045,
    realWorldImpact: "Creates workplace conflicts, relationship problems, and most of the drama in your friend group."
  },
  {
    id: 12,
    name: "In-group Bias",
    shortDescription: "My people are obviously superior to your people",
    satiricalQuote: "We're not biased, we're just better. It's everyone else who has the problem.",
    description: "The tendency to favour members of one's own group over others. Tribalism with a psychology degree.",
    modernExample: "Thinking your company's mediocre software is innovative while dismissing superior competitors as 'overhyped.'",
    severity: "High",
    category: "Social Influence",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Henri Tajfel (1970)",
    funFact: "People show in-group bias even when assigned to groups based on completely arbitrary criteria, like coin flips.",
    interactionCount: 1267,
    realWorldImpact: "Fuels everything from sports rivalries to political polarisation to workplace politics."
  },
  {
    id: 13,
    name: "Out-group Homogeneity Bias",
    shortDescription: "They all look the same to me",
    satiricalQuote: "Our group has amazing diversity. Their group? They're basically clones.",
    description: "The tendency to perceive out-group members as more similar to each other than they really are.",
    modernExample: "Thinking all millennials are the same while recognising the vast differences among people in your own generation.",
    severity: "Medium",
    category: "Social Perception",
    algorithmicAmplification: "High",
    discoveredBy: "Bernd Simon & Rupert Brown (1987)",
    funFact: "This bias helps explain stereotyping and why people say 'they all look alike' about other ethnicities but easily distinguish faces within their own.",
    interactionCount: 634,
    realWorldImpact: "Contributes to stereotyping, prejudice, and the failure to recognise diversity within other groups."
  },
  {
    id: 14,
    name: "Just-World Hypothesis",
    shortDescription: "Good things happen to good people, right?",
    satiricalQuote: "If bad things happen to you, you probably deserved it. The universe is basically a cosmic judge.",
    description: "The belief that the world is fair and people get what they deserve. Karma's philosophical cousin.",
    modernExample: "Assuming someone who got fired 'must have done something wrong' rather than considering economic factors or bad management.",
    severity: "High",
    category: "Attribution",
    algorithmicAmplification: "Medium",
    discoveredBy: "Melvin Lerner (1980)",
    funFact: "This bias helps people feel safer by believing bad things won't happen to them if they're good, but it also leads to victim blaming.",
    interactionCount: 789,
    realWorldImpact: "Leads to victim blaming, reduces empathy for those suffering misfortune, and creates unrealistic expectations about fairness."
  },
  {
    id: 15,
    name: "Halo Effect",
    shortDescription: "Beautiful people must be smart, right?",
    satiricalQuote: "If you're good at one thing, you're obviously good at everything. Logic has never been simpler.",
    description: "The tendency to let an overall impression influence judgements about specific traits.",
    modernExample: "Assuming a successful CEO must be great at parenting, cooking, and relationship advice because they're good at business.",
    severity: "High",
    category: "Social Perception",
    algorithmicAmplification: "High",
    discoveredBy: "Edward Thorndike (1920)",
    funFact: "The halo effect is so strong that attractive defendants get lighter sentences in court, and tall people are more likely to become CEOs.",
    interactionCount: 1123,
    realWorldImpact: "Influences hiring decisions, performance reviews, and explains why celebrities think they're experts on everything."
  },
  {
    id: 16,
    name: "Horn Effect",
    shortDescription: "One bad trait means everything is bad",
    satiricalQuote: "If I don't like how you dress, I probably won't like your personality either. Efficiency!",
    description: "The tendency to let a negative impression influence judgements about all other traits. The halo effect's evil twin.",
    modernExample: "Thinking someone who's chronically late is also probably dishonest, unreliable, and bad at their job.",
    severity: "Medium",
    category: "Social Perception",
    algorithmicAmplification: "Medium",
    discoveredBy: "Edward Thorndike (1920)",
    funFact: "The horn effect can completely override positive traits, which is why first impressions are so crucial and so unfair.",
    interactionCount: 567,
    realWorldImpact: "Creates unfair negative evaluations and can torpedo relationships before they start."
  },
  {
    id: 17,
    name: "Status Quo Bias",
    shortDescription: "Change is scary, let's just keep things as they are",
    satiricalQuote: "Why improve things when mediocrity is so comfortable?",
    description: "The preference for the current state of affairs. The psychological equivalent of 'if it ain't broke, don't fix it.'",
    modernExample: "Keeping the same inefficient work process for years because 'that's how we've always done it' despite obvious better alternatives.",
    severity: "Medium",
    category: "Decision Making",
    algorithmicAmplification: "Low",
    discoveredBy: "William Samuelson & Richard Zeckhauser (1988)",
    funFact: "Status quo bias is why most people never change their default settings and why companies make opting out deliberately difficult.",
    interactionCount: 743,
    realWorldImpact: "Prevents innovation, maintains inefficient systems, and keeps people in situations that no longer serve them."
  },
  {
    id: 18,
    name: "Framing Effect",
    shortDescription: "Same facts, different feelings",
    satiricalQuote: "Why tell the truth plainly when you can spin it to your advantage?",
    description: "When decisions are influenced by how information is presented rather than just the information itself.",
    modernExample: "Being more likely to buy ground beef labelled '85% lean' than '15% fat' even though they're identical.",
    severity: "High",
    category: "Decision Making",
    algorithmicAmplification: "Very High",
    discoveredBy: "Amos Tversky & Daniel Kahneman (1981)",
    funFact: "Politicians, marketers, and lawyers are professional framers. The same policy can sound amazing or terrible depending on the words used.",
    interactionCount: 1234,
    realWorldImpact: "Manipulates consumer choices, political opinions, and medical decisions. Words literally change reality."
  },
  {
    id: 19,
    name: "Loss Aversion",
    shortDescription: "Losing $100 hurts more than winning $100 feels good",
    satiricalQuote: "The fear of losing what you have is stronger than the joy of gaining something new. Economics meets anxiety.",
    description: "Greater sensitivity to losses than to equivalent gains. Your brain's built-in pessimist.",
    modernExample: "Refusing to switch to a better phone plan because you might 'lose' your current number, even though the savings would be significant.",
    severity: "High",
    category: "Economic",
    algorithmicAmplification: "High",
    discoveredBy: "Daniel Kahneman & Amos Tversky (1979)",
    funFact: "Losses are felt about twice as strongly as equivalent gains. This is why 'limited time offers' and 'you might lose out' marketing works so well.",
    interactionCount: 1445,
    realWorldImpact: "Influences investment decisions, keeps people in bad situations, and drives most marketing strategies."
  },
  {
    id: 20,
    name: "Gambler's Fallacy",
    shortDescription: "The dice must be due for a change",
    satiricalQuote: "The universe definitely keeps track of coin flips to ensure fairness. Basic cosmic justice.",
    description: "The belief that past random events affect future probabilities. Statistics' nemesis.",
    modernExample: "Thinking that after five heads in a row, the next coin flip is 'due' to be tails, when it's still exactly 50-50.",
    severity: "Medium",
    category: "Probability Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Amos Tversky & Daniel Kahneman (1974)",
    funFact: "This fallacy keeps casinos in business and explains why people buy lottery tickets with 'cold' numbers that haven't come up recently.",
    interactionCount: 678,
    realWorldImpact: "Drives gambling addiction, bad investment strategies, and the lottery industry's profits."
  },
  {
    id: 21,
    name: "Survivorship Bias",
    shortDescription: "We only hear success stories, not failure statistics",
    satiricalQuote: "Successful entrepreneurs dropped out of college, so dropping out guarantees success, right?",
    description: "Focusing on successful cases while ignoring failures. History's selective editor.",
    modernExample: "Thinking college dropouts become billionaires because you hear about Gates and Jobs, but not the millions who dropped out and struggled.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "Very High",
    discoveredBy: "Abraham Wald (1940s)",
    funFact: "This bias got its name from WWII when researchers studied bullet holes in returning planes, not realising they should focus on where the missing planes were hit.",
    interactionCount: 1089,
    realWorldImpact: "Creates unrealistic expectations, bad business advice, and explains why self-help books all sound so convincing."
  },
  {
    id: 22,
    name: "Actor-Observer Bias",
    shortDescription: "I have reasons, you have personality flaws",
    satiricalQuote: "My behaviour is situational and complex. Yours is obviously just who you are as a person.",
    description: "Attributing one's own behaviour to situational factors and others' behaviour to dispositional factors.",
    modernExample: "When you're on your phone during a meeting, you're 'handling an urgent client issue.' When colleagues do it, they're 'distracted and unprofessional.'",
    severity: "High",
    category: "Attribution",
    algorithmicAmplification: "Medium",
    discoveredBy: "Edward Jones & Richard Nisbett (1972)",
    funFact: "This bias exists because you have access to your own thoughts and circumstances, but can only see others' actions. Perspective is everything.",
    interactionCount: 834,
    realWorldImpact: "Creates relationship conflicts, workplace tensions, and most arguments between drivers."
  },
  {
    id: 23,
    name: "False Consensus Effect",
    shortDescription: "Everyone thinks like me... right?",
    satiricalQuote: "My opinions are obviously mainstream. Anyone who disagrees is clearly in a weird minority.",
    description: "Overestimating how much others share one's beliefs or behaviours. Democracy's overconfident cousin.",
    modernExample: "Thinking 'everyone knows' your favourite TV show, political opinion, or life philosophy, when you actually live in a bubble.",
    severity: "Medium",
    category: "Social Perception",
    algorithmicAmplification: "Very High",
    discoveredBy: "Lee Ross, David Greene & Pamela House (1977)",
    funFact: "Social media algorithms amplify this bias by showing you content similar to your interests, making your bubble feel like the entire world.",
    interactionCount: 967,
    realWorldImpact: "Creates political echo chambers, marketing blind spots, and explains why everyone is 'shocked' by election results."
  },
  {
    id: 24,
    name: "Illusory Correlation",
    shortDescription: "Seeing patterns where none exist",
    satiricalQuote: "Correlation definitely implies causation, especially when it confirms what I already believe.",
    description: "Perceiving a relationship between variables when none exists. Statistics' most creative enemy.",
    modernExample: "Thinking your team always loses when you wear your lucky shirt, despite there being no actual correlation between clothing and sports outcomes.",
    severity: "Medium",
    category: "Information Processing",
    algorithmicAmplification: "High",
    discoveredBy: "Loren Chapman & Jean Chapman (1967)",
    funFact: "This bias explains why people believe in superstitions, lucky charms, and why astrology feels 'accurate' to believers.",
    interactionCount: 756,
    realWorldImpact: "Fuels superstitious behaviour, false scientific conclusions, and conspiracy theories."
  },
  {
    id: 25,
    name: "Planning Fallacy",
    shortDescription: "This time I'll definitely finish on schedule",
    satiricalQuote: "Past performance is no indicator of future results, especially when it comes to my time management.",
    description: "Underestimating the time or resources needed to complete a task. Optimism meets reality badly.",
    modernExample: "Thinking you'll finish that project in 2 hours when similar projects have always taken you 6 hours minimum.",
    severity: "High",
    category: "Decision Making",
    algorithmicAmplification: "Low",
    discoveredBy: "Daniel Kahneman & Amos Tversky (1979)",
    funFact: "Even when people know about this bias, they still fall victim to it. Self-awareness doesn't cure optimistic delusion.",
    interactionCount: 1123,
    realWorldImpact: "Causes project delays, budget overruns, and explains why construction projects never finish on time."
  },
  {
    id: 26,
    name: "Reactance",
    shortDescription: "Tell me I can't do something and suddenly I really want to do it",
    satiricalQuote: "The fastest way to make me want something is to tell me I can't have it. Reverse psychology 101.",
    description: "Resistance to persuasion or rules perceived as threats to freedom. The psychological equivalent of teenage rebellion.",
    modernExample: "Suddenly craving junk food when you start a diet, or wanting to check social media the moment your phone is confiscated.",
    severity: "Medium",
    category: "Social Influence",
    algorithmicAmplification: "Medium",
    discoveredBy: "Jack Brehm (1966)",
    funFact: "Marketers exploit this by creating 'exclusive' or 'limited access' products, making people want them more by restricting availability.",
    interactionCount: 645,
    realWorldImpact: "Drives rebellious behaviour, makes restrictions backfire, and explains why 'forbidden fruit' is so appealing."
  },
  {
    id: 27,
    name: "Representativeness Heuristic",
    shortDescription: "If it looks like a duck, it must be a duck",
    satiricalQuote: "Why use actual statistics when stereotypes are so much faster?",
    description: "Judging probabilities based on similarity to a prototype rather than actual statistics.",
    modernExample: "Assuming someone who's quiet and likes books must be a librarian rather than considering there are far more accountants, engineers, and other professions.",
    severity: "High",
    category: "Probability Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Daniel Kahneman & Amos Tversky (1974)",
    funFact: "This heuristic makes people terrible at estimating base rates. We judge by how 'typical' something seems, not how common it actually is.",
    interactionCount: 889,
    realWorldImpact: "Creates stereotyping, bad hiring decisions, and explains why profiling feels 'intuitive' but is often wrong."
  },
  {
    id: 28,
    name: "Selective Perception",
    shortDescription: "I see what I want to see",
    satiricalQuote: "Reality is optional. I prefer the customised version that matches my beliefs.",
    description: "Noticing information that confirms beliefs and ignoring contradictory data. Confirmation bias's more subtle cousin.",
    modernExample: "Noticing every article that supports your political views while somehow missing all the opposing evidence in the same news feed.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Jerome Bruner & Leo Postman (1949)",
    funFact: "This bias is so strong that people can literally not see things that contradict their expectations, even when looking directly at them.",
    interactionCount: 1234,
    realWorldImpact: "Reinforces existing beliefs, creates filter bubbles, and makes changing minds nearly impossible."
  },
  {
    id: 29,
    name: "Spotlight Effect",
    shortDescription: "Everyone is definitely watching and judging me",
    satiricalQuote: "I'm obviously the main character in everyone else's story. They're all thinking about my outfit.",
    description: "Overestimating the extent to which one's actions are noticed by others.",
    modernExample: "Thinking everyone will notice and remember that small stain on your shirt, when most people are too busy worrying about their own appearance.",
    severity: "Medium",
    category: "Social Perception",
    algorithmicAmplification: "High",
    discoveredBy: "Thomas Gilovich, Victoria Medvec & Kenneth Savitsky (2000)",
    funFact: "People think others notice their mistakes about twice as much as they actually do. Your embarrassing moments are mostly invisible to others.",
    interactionCount: 567,
    realWorldImpact: "Causes social anxiety, self-consciousness, and explains why public speaking feels so terrifying."
  },
  {
    id: 30,
    name: "Curse of Knowledge",
    shortDescription: "Once you know something, you forget what it's like not to know it",
    satiricalQuote: "This is so obvious! Why doesn't everyone understand what's clearly simple?",
    description: "Difficulty in imagining the perspective of someone less informed. Expertise's empathy problem.",
    modernExample: "Tech experts explaining software by using jargon and assuming users know basics, then getting frustrated when 'simple' instructions aren't followed.",
    severity: "Medium",
    category: "Communication",
    algorithmicAmplification: "Low",
    discoveredBy: "Colin Camerer, George Loewenstein & Martin Weber (1989)",
    funFact: "This bias makes experts terrible teachers unless they consciously fight against it. Knowledge creates a blind spot for ignorance.",
    interactionCount: 723,
    realWorldImpact: "Creates communication barriers, bad user interfaces, and explains why expert advice often seems useless."
  },
  {
    id: 31,
    name: "Belief Bias",
    shortDescription: "Logic is optional if the conclusion matches my beliefs",
    satiricalQuote: "Why think critically when I can just check if the conclusion feels right?",
    description: "Evaluating logical arguments based on their believability rather than validity.",
    modernExample: "Accepting weak arguments for positions you already support while demanding perfect logic for opposing viewpoints.",
    severity: "High",
    category: "Reasoning",
    algorithmicAmplification: "High",
    discoveredBy: "Jonathan Evans, Julie Barston & Paul Pollard (1983)",
    funFact: "Even trained logicians fall victim to this bias. Our brains prioritise conclusions over the process of reaching them.",
    interactionCount: 845,
    realWorldImpact: "Undermines critical thinking, reinforces polarisation, and makes logical discourse nearly impossible."
  },
  {
    id: 32,
    name: "Blind Spot Bias",
    shortDescription: "Everyone else is biased, but not me",
    satiricalQuote: "I'm uniquely rational and objective. It's everyone else who has cognitive biases.",
    description: "Recognising biases in others but failing to see them in oneself. Meta-bias at its finest.",
    modernExample: "Criticising others for confirmation bias while simultaneously seeking out news sources that align with your own political views.",
    severity: "High",
    category: "Self-Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Emily Pronin, Daniel Lin & Lee Ross (2002)",
    funFact: "The more you learn about cognitive biases, the more you see them in others and the less you see them in yourself. Knowledge creates overconfidence.",
    interactionCount: 1067,
    realWorldImpact: "Prevents self-improvement, maintains intellectual arrogance, and makes bias training ironically less effective."
  },
  {
    id: 33,
    name: "Choice-supportive Bias",
    shortDescription: "The grass is greener on the side I chose",
    satiricalQuote: "My decisions are always the best possible ones. Evidence to the contrary is clearly flawed.",
    description: "Remembering one's choices as better than they actually were. Buyer's remorse's optimistic cousin.",
    modernExample: "Convincing yourself that the expensive coffee you bought was 'definitely worth it' while downplaying any negatives about your choice.",
    severity: "Medium",
    category: "Memory",
    algorithmicAmplification: "Medium",
    discoveredBy: "Jack Brehm (1956)",
    funFact: "This bias kicks in within minutes of making a decision. Your brain literally rewrites your memory to make you feel better about your choices.",
    interactionCount: 634,
    realWorldImpact: "Prevents learning from poor decisions, maintains consumer satisfaction, and explains brand loyalty."
  },
  {
    id: 34,
    name: "Clustering Illusion",
    shortDescription: "Random patterns must mean something",
    satiricalQuote: "The universe is definitely sending me signs through random events. Coincidence is just lazy thinking.",
    description: "Seeing patterns in random data. Statistics' most persistent hallucination.",
    modernExample: "Thinking a basketball player is 'on fire' because of a few consecutive shots, when it's actually just normal random variation.",
    severity: "Medium",
    category: "Probability Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Thomas Gilovich, Robert Vallone & Amos Tversky (1985)",
    funFact: "Humans are so bad at recognising randomness that truly random sequences look 'too clustered' to us, while we think fake 'random' patterns are more realistic.",
    interactionCount: 456,
    realWorldImpact: "Fuels gambling addiction, conspiracy theories, and the belief in 'hot streaks' in sports and investing."
  },
  {
    id: 35,
    name: "Endowment Effect",
    shortDescription: "My stuff is worth more because it's mine",
    satiricalQuote: "Once I own something, it magically becomes more valuable. Ownership is an enchantment spell.",
    description: "Valuing owned objects more than equivalent objects not owned. Possession creates irrational attachment.",
    modernExample: "Refusing to sell your car for what you'd never pay to buy the same car, simply because you currently own it.",
    severity: "High",
    category: "Economic",
    algorithmicAmplification: "Medium",
    discoveredBy: "Richard Thaler (1980)",
    funFact: "This effect appears almost immediately upon ownership and is strong enough to significantly distort market prices and prevent beneficial trades.",
    interactionCount: 987,
    realWorldImpact: "Reduces market efficiency, prevents beneficial exchanges, and explains why people hoard items they never use."
  },
  {
    id: 36,
    name: "IKEA Effect",
    shortDescription: "I built it, therefore it's amazing",
    satiricalQuote: "Anything I partially create becomes a masterpiece. Sweat equity equals artistic value.",
    description: "Overvaluing products one has partially created. DIY becomes psychological ownership on steroids.",
    modernExample: "Thinking your assembled IKEA furniture is better than pre-made furniture, or believing your homemade meal is restaurant-quality.",
    severity: "Medium",
    category: "Economic",
    algorithmicAmplification: "Low",
    discoveredBy: "Michael Norton, Daniel Mochon & Dan Ariely (2012)",
    funFact: "Named after the Swedish furniture company, this bias explains why people love crafts, cooking, and any 'build-your-own' products.",
    interactionCount: 678,
    realWorldImpact: "Drives the DIY industry, makes people overvalue their own work, and explains the popularity of customization."
  },
  {
    id: 37,
    name: "Mere Exposure Effect",
    shortDescription: "Familiar equals good",
    satiricalQuote: "The more I see something, the more I like it. Repetition is the mother of affection.",
    description: "Developing preference for things simply because they are familiar. Marketing's best friend.",
    modernExample: "Gradually liking a song you initially hated just because it plays everywhere, or warming up to coworkers you see daily.",
    severity: "Medium",
    category: "Social Influence",
    algorithmicAmplification: "Very High",
    discoveredBy: "Robert Zajonc (1968)",
    funFact: "This effect works even with subliminal exposure. You can develop preferences for things you don't consciously remember seeing.",
    interactionCount: 1145,
    realWorldImpact: "Drives brand recognition, explains why advertising works, and influences everything from mate selection to political preferences."
  },
  {
    id: 38,
    name: "Neglect of Probability",
    shortDescription: "Unlikely events feel impossible, possible events feel certain",
    satiricalQuote: "Math is hard. Let's just go with our gut feeling about how likely things are.",
    description: "Ignoring or underweighting probability when making decisions. Statistics meet intuition badly.",
    modernExample: "Buying lottery tickets (extremely low probability) while refusing to wear seatbelts (moderately high probability of accident).",
    severity: "High",
    category: "Probability Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Cass Sunstein (2002)",
    funFact: "People treat 1% and 0.01% probabilities almost the same way, which explains both unnecessary anxiety and dangerous overconfidence.",
    interactionCount: 534,
    realWorldImpact: "Drives irrational fears, poor insurance decisions, and explains why people both gamble and worry about plane crashes."
  },
  {
    id: 39,
    name: "Outcome Bias",
    shortDescription: "Results matter more than the decision process",
    satiricalQuote: "Good outcome equals good decision. Bad outcome equals bad decision. Simple math.",
    description: "Judging a decision based on its outcome rather than the quality of the decision at the time.",
    modernExample: "Calling a risky investment 'brilliant' because it happened to pay off, while ignoring that it was statistically a bad bet.",
    severity: "High",
    category: "Decision Making",
    algorithmicAmplification: "Low",
    discoveredBy: "Jonathan Baron & John Hershey (1988)",
    funFact: "This bias makes us terrible at learning from experience because we focus on results rather than process, making us repeat good luck and avoid good strategy.",
    interactionCount: 789,
    realWorldImpact: "Prevents learning from experience, rewards lucky idiots, and punishes unlucky experts."
  },
  {
    id: 40,
    name: "Overconfidence Effect",
    shortDescription: "I'm right more often than statistics suggest I should be",
    satiricalQuote: "Confidence is its own evidence. If I feel certain, I must be correct.",
    description: "Excessive confidence in one's own answers or judgments. Certainty without accuracy.",
    modernExample: "Being '90% sure' about answers you get right only 70% of the time, or confidently giving directions to a place you've been once.",
    severity: "High",
    category: "Self-Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Stuart Oskamp (1965)",
    funFact: "The more information people have, the more overconfident they become, even when the extra information is irrelevant or misleading.",
    interactionCount: 1267,
    realWorldImpact: "Causes poor decision-making, explains why experts are often wrong with great confidence, and drives financial bubbles."
  },
  {
    id: 41,
    name: "Peak-End Rule",
    shortDescription: "How it ended is how I remember the whole thing",
    satiricalQuote: "Why remember the entire experience when I can just focus on the most intense moment and the ending?",
    description: "Judging experiences largely based on their peak moments and how they end.",
    modernExample: "Rating a vacation based on the best day and the last day, while forgetting about several mediocre days in between.",
    severity: "Medium",
    category: "Memory",
    algorithmicAmplification: "Medium",
    discoveredBy: "Daniel Kahneman & Barbara Fredrickson (1993)",
    funFact: "A shorter pleasant experience can be remembered as better than a longer one if it has a better ending, even with less total pleasure.",
    interactionCount: 645,
    realWorldImpact: "Influences customer satisfaction, relationship memories, and explains why endings are disproportionately important."
  },
  {
    id: 42,
    name: "Recency Effect",
    shortDescription: "The last thing I heard is the most important",
    satiricalQuote: "Recent information is obviously more relevant. Why would I remember old news?",
    description: "Greater recall or influence of the most recent information. Memory's recency bias.",
    modernExample: "Basing your opinion of a restaurant entirely on your last visit, forgetting years of previous good experiences after one bad meal.",
    severity: "Medium",
    category: "Memory",
    algorithmicAmplification: "Very High",
    discoveredBy: "Hermann Ebbinghaus (1885)",
    funFact: "This bias combines with social media algorithms to make recent events seem more important than they actually are in the broader context.",
    interactionCount: 834,
    realWorldImpact: "Distorts historical perspective, makes recent events seem more significant, and influences voting behaviour near elections."
  },
  {
    id: 43,
    name: "Semmelweis Reflex",
    shortDescription: "New ideas are dangerous until they're obviously correct",
    satiricalQuote: "We've always done it this way. Innovation is just a fancy word for troublemaking.",
    description: "Rejection of new knowledge because it contradicts established norms. Tradition's immune system.",
    modernExample: "Medical professionals initially rejecting hand-washing or new treatments because they challenge established practices.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "Low",
    discoveredBy: "Robert Anton Wilson (1977)",
    funFact: "Named after Ignaz Semmelweis, who was ridiculed for suggesting doctors wash their hands, even though it dramatically reduced patient deaths.",
    interactionCount: 456,
    realWorldImpact: "Slows scientific progress, maintains harmful practices, and explains why beneficial innovations face resistance."
  },
  {
    id: 44,
    name: "Social Desirability Bias",
    shortDescription: "I'll tell you what you want to hear",
    satiricalQuote: "Why be honest when I can be likable? Truth is less important than social approval.",
    description: "Responding in ways perceived to be socially acceptable rather than truthful.",
    modernExample: "Claiming to eat healthier, exercise more, or be more charitable than you actually are when asked in surveys or social situations.",
    severity: "Medium",
    category: "Social Influence",
    algorithmicAmplification: "High",
    discoveredBy: "Allen Edwards (1957)",
    funFact: "This bias makes most survey data about sensitive topics unreliable, which is why anonymous surveys often show dramatically different results.",
    interactionCount: 723,
    realWorldImpact: "Skews research data, creates false social norms, and explains why people seem better on social media than in reality."
  },
  {
    id: 45,
    name: "Zero-Risk Bias",
    shortDescription: "Eliminate one tiny risk completely rather than reduce overall risk significantly",
    satiricalQuote: "I prefer zero risk in one area to lower risk everywhere. Math is clearly optional in safety decisions.",
    description: "Preference for eliminating risk entirely in one area rather than reducing overall risk.",
    modernExample: "Spending huge amounts to eliminate a 1% risk while ignoring ways to reduce a 20% risk, because zero feels safer than 'reduced.'",
    severity: "Medium",
    category: "Risk Assessment",
    algorithmicAmplification: "Low",
    discoveredBy: "Amos Tversky & Daniel Kahneman (1991)",
    funFact: "This bias explains why people support expensive policies that eliminate small risks while opposing cost-effective policies that reduce larger risks.",
    interactionCount: 567,
    realWorldImpact: "Leads to inefficient allocation of safety resources and explains why 'zero tolerance' policies are popular but often counterproductive."
  },
  {
    id: 46,
    name: "Ambiguity Effect",
    shortDescription: "When in doubt, avoid it completely",
    satiricalQuote: "Why take a chance when you can just avoid making any decision at all?",
    description: "Avoiding options with unknown probabilities. Uncertainty is scary, so we'd rather stick with the devil we know.",
    modernExample: "Refusing to invest in a promising startup because you can't calculate exact success probability, while keeping money in a 0.1% savings account.",
    severity: "Medium",
    category: "Decision Making",
    algorithmicAmplification: "Medium",
    discoveredBy: "Daniel Ellsberg (1961)",
    funFact: "This bias explains why people buy insurance for unlikely events but won't invest in potentially profitable opportunities.",
    interactionCount: 634,
    realWorldImpact: "Prevents innovation adoption, reduces investment in beneficial but uncertain opportunities, and maintains status quo."
  },
  {
    id: 47,
    name: "Authority Bias",
    shortDescription: "Important people must be right about everything",
    satiricalQuote: "If someone has authority in one area, they're obviously experts in all areas. Logic!",
    description: "Valuing the opinion of an authority figure disproportionately, even outside their area of expertise.",
    modernExample: "Taking medical advice from a celebrity doctor on social media, or believing a tech billionaire's opinions about politics and society.",
    severity: "High",
    category: "Social Influence",
    algorithmicAmplification: "Very High",
    discoveredBy: "Stanley Milgram (1963)",
    funFact: "This bias is so strong that people will follow obviously wrong directions from authority figures, even when it contradicts their own knowledge.",
    interactionCount: 1345,
    realWorldImpact: "Enables manipulation by unqualified authorities, suppresses critical thinking, and explains why celebrity endorsements work."
  },
  {
    id: 48,
    name: "Backfire Effect",
    shortDescription: "Contradictory evidence makes me believe harder",
    satiricalQuote: "Your facts just prove how right I am. The more you argue, the more convinced I become.",
    description: "Strengthening of beliefs when confronted with contradictory evidence. Evidence against your position is obviously fake news.",
    modernExample: "Becoming more convinced of a conspiracy theory after reading scientific studies debunking it, interpreting the debunking as proof of the cover-up.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Brendan Nyhan & Jason Reifler (2010)",
    funFact: "This effect is stronger for political and religious beliefs, making fact-checking sometimes counterproductive.",
    interactionCount: 1156,
    realWorldImpact: "Makes misinformation nearly impossible to correct and explains why political polarisation increases with more information."
  },
  {
    id: 49,
    name: "Base Rate Fallacy",
    shortDescription: "Individual stories beat boring statistics every time",
    satiricalQuote: "Statistics are for other people. This specific case is obviously different.",
    description: "Ignoring statistical base rates in favour of individuating information. Personal anecdotes always trump data.",
    modernExample: "Fearing flying after hearing about one plane crash while ignoring that you're statistically safer than driving to the airport.",
    severity: "High",
    category: "Probability Assessment",
    algorithmicAmplification: "High",
    discoveredBy: "Daniel Kahneman & Amos Tversky (1973)",
    funFact: "This fallacy explains why people buy lottery tickets but don't buy insurance, and why medical students diagnose rare diseases.",
    interactionCount: 892,
    realWorldImpact: "Leads to poor risk assessment, medical misdiagnosis, and explains why personal stories trump statistical evidence."
  },
  {
    id: 50,
    name: "Belief Perseverance",
    shortDescription: "My beliefs are stronger than your evidence",
    satiricalQuote: "I've held this belief for years. I'm not changing now just because of 'facts.'",
    description: "Maintaining beliefs despite disconfirming evidence. Beliefs are investments, and we don't like losing money.",
    modernExample: "Continuing to believe a discredited health fad worked for you even after studies prove it's ineffective, because you 'felt better' while doing it.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "High",
    discoveredBy: "Lee Ross, Mark Lepper & Michael Hubbard (1975)",
    funFact: "People will maintain beliefs even when told the original evidence was completely fabricated and shown the fabrication process.",
    interactionCount: 967,
    realWorldImpact: "Prevents learning from mistakes, maintains harmful practices, and makes education and persuasion extremely difficult."
  },
  {
    id: 51,
    name: "Cheerleader Effect",
    shortDescription: "Everyone looks better with friends",
    satiricalQuote: "Individual photos are so harsh. Group photos make everyone mysteriously more attractive.",
    description: "Perceiving individuals as more attractive in a group context than when alone.",
    modernExample: "Swiping right on dating apps because someone looks great in group photos, then being disappointed when you see their solo shots.",
    severity: "Low",
    category: "Social Perception",
    algorithmicAmplification: "High",
    discoveredBy: "Drew Walker & Edward Vul (2014)",
    funFact: "This effect works because our brains average faces in groups, and average faces are considered more attractive.",
    interactionCount: 423,
    realWorldImpact: "Influences dating decisions, hiring choices based on team photos, and explains why group marketing photos are so common."
  },
  {
    id: 52,
    name: "Conservatism Bias",
    shortDescription: "New evidence is just a suggestion",
    satiricalQuote: "I'll update my beliefs, but very, very slowly. Like geological time slowly.",
    description: "Insufficiently revising beliefs when presented with new evidence. Change is hard, so let's just make tiny adjustments.",
    modernExample: "Still thinking someone is unreliable based on one bad experience years ago, despite dozens of recent examples of their reliability.",
    severity: "Medium",
    category: "Information Processing",
    algorithmicAmplification: "Medium",
    discoveredBy: "Ward Edwards (1968)",
    funFact: "People typically update their beliefs by only 10-20% of what they should based on new evidence, leading to persistent misconceptions.",
    interactionCount: 745,
    realWorldImpact: "Slows adaptation to new information, maintains outdated stereotypes, and prevents learning from feedback."
  },
  {
    id: 53,
    name: "Decoy Effect",
    shortDescription: "Suddenly the expensive option looks reasonable",
    satiricalQuote: "I wasn't going to buy the premium version until you showed me the ridiculously overpriced deluxe version.",
    description: "Preference changes caused by adding a less attractive option. Marketing's favourite psychological trick.",
    modernExample: "Choosing the large popcorn at movies because the medium is only 50 cents less, even though you originally wanted small.",
    severity: "High",
    category: "Decision Making",
    algorithmicAmplification: "High",
    discoveredBy: "Joel Huber, John Payne & Christopher Puto (1982)",
    funFact: "This effect is so reliable that companies design entire product lines around it, creating 'decoy' products they never expect to sell.",
    interactionCount: 1234,
    realWorldImpact: "Manipulates consumer choices, increases spending on unnecessary upgrades, and explains why 'premium' options exist."
  },
  {
    id: 54,
    name: "Deformation Professionnelle",
    shortDescription: "When all you have is a hammer, everything looks like a nail",
    satiricalQuote: "My profession has taught me to see the world correctly. Everyone else is just missing the obvious solution.",
    description: "Viewing problems through the lens of one's profession or expertise, regardless of context.",
    modernExample: "Software engineers suggesting 'we need an app for that' for every social problem, or lawyers seeing legal solutions to relationship issues.",
    severity: "Medium",
    category: "Professional Bias",
    algorithmicAmplification: "Low",
    discoveredBy: "Thorstein Veblen (1914)",
    funFact: "This bias explains why interdisciplinary teams often outperform expert teams - diverse perspectives prevent tunnel vision.",
    interactionCount: 567,
    realWorldImpact: "Limits problem-solving approaches, creates professional echo chambers, and explains why expert advice can be narrow."
  },
  {
    id: 55,
    name: "Egocentric Bias",
    shortDescription: "I'm obviously the main character in this story",
    satiricalQuote: "Group projects succeed because of my contributions. They fail because of everyone else's lack of contribution.",
    description: "Overestimating one's own contribution or importance in group efforts or relationships.",
    modernExample: "Thinking you did 70% of the household chores when your partner also thinks they did 70% of the household chores.",
    severity: "High",
    category: "Self-Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Baruch Fischhoff & Ruth Beyth (1975)",
    funFact: "In studies of group work, individual estimates of personal contribution typically add up to 140-150% of the total work done.",
    interactionCount: 1089,
    realWorldImpact: "Creates relationship conflicts, workplace disputes, and explains why everyone thinks they're doing more than their fair share."
  },
  {
    id: 56,
    name: "Empathy Gap",
    shortDescription: "Future me will be much more rational",
    satiricalQuote: "When I'm calm, I can't imagine being angry. When I'm angry, I can't imagine being calm. Emotions are temporary, right?",
    description: "Underestimating the influence of emotional states on behaviour, both for ourselves and others.",
    modernExample: "Going grocery shopping while hungry and buying way too much junk food, despite promising yourself you'd eat healthy.",
    severity: "High",
    category: "Emotional Bias",
    algorithmicAmplification: "Medium",
    discoveredBy: "George Loewenstein (1996)",
    funFact: "People consistently underestimate how much their emotional state affects their decisions, leading to poor planning and self-control.",
    interactionCount: 834,
    realWorldImpact: "Leads to poor self-control, inadequate preparation for emotional states, and misunderstanding others' motivations."
  },
  {
    id: 57,
    name: "End-of-History Illusion",
    shortDescription: "I'm finally done changing as a person",
    satiricalQuote: "I've changed so much in the past, but now I'm basically finished evolving. This is the final me.",
    description: "Believing one has changed a lot in the past but will change little in the future.",
    modernExample: "Thinking your current music taste, political views, and personality are permanent, despite them being completely different from 10 years ago.",
    severity: "Medium",
    category: "Self-Assessment",
    algorithmicAmplification: "Low",
    discoveredBy: "Jordi Quoidbach, Daniel Gilbert & Timothy Wilson (2013)",
    funFact: "This illusion persists at every age - teenagers, adults, and seniors all think they're done changing, despite evidence to the contrary.",
    interactionCount: 456,
    realWorldImpact: "Prevents long-term planning, creates unrealistic expectations about consistency, and limits personal growth efforts."
  },
  {
    id: 58,
    name: "Expectation Bias",
    shortDescription: "I see what I expect to see",
    satiricalQuote: "Why let reality interfere with what I already know should happen?",
    description: "Influencing outcomes based on expectations. Our predictions become self-fulfilling prophecies.",
    modernExample: "A teacher expecting certain students to perform poorly, then unconsciously giving them less attention and lower grades, confirming the expectation.",
    severity: "High",
    category: "Perception",
    algorithmicAmplification: "Medium",
    discoveredBy: "Robert Rosenthal & Lenore Jacobson (1968)",
    funFact: "This bias is so strong it affects scientific research - researchers often find results that match their hypotheses even when analysing the same data.",
    interactionCount: 923,
    realWorldImpact: "Creates educational inequities, biased research results, and explains how stereotypes perpetuate themselves."
  },
  {
    id: 59,
    name: "Feature Positive Effect",
    shortDescription: "Something is always more interesting than nothing",
    satiricalQuote: "I notice what's there, not what's missing. Absence of evidence is just... absent.",
    description: "Paying more attention to the presence rather than absence of features or information.",
    modernExample: "Focusing on the ingredients listed in 'natural' food products while ignoring what's NOT listed or what nutrients are missing.",
    severity: "Medium",
    category: "Attention",
    algorithmicAmplification: "Medium",
    discoveredBy: "Peter Hearst (1991)",
    funFact: "This bias explains why people are bad at noticing what's missing from presentations, contracts, or arguments.",
    interactionCount: 567,
    realWorldImpact: "Leads to incomplete analysis, overlooking critical missing information, and falling for marketing that highlights positives while hiding negatives."
  },
  {
    id: 60,
    name: "Focusing Effect",
    shortDescription: "This one thing determines my entire future happiness",
    satiricalQuote: "If I could just fix this one aspect of my life, everything would be perfect forever.",
    description: "Overemphasising one aspect of an event when predicting future feelings. Single factors dominate our predictions.",
    modernExample: "Thinking that getting a promotion will make you permanently happier, while ignoring all other factors that affect well-being.",
    severity: "High",
    category: "Emotional Prediction",
    algorithmicAmplification: "Medium",
    discoveredBy: "Daniel Kahneman & David Schkade (1998)",
    funFact: "People consistently overestimate how much any single change (winning lottery, losing weight, moving cities) will affect their long-term happiness.",
    interactionCount: 789,
    realWorldImpact: "Leads to poor life decisions, overinvestment in single solutions, and explains why achieving goals often feels less satisfying than expected."
  },
  {
    id: 61,
    name: "Forer Effect",
    shortDescription: "This generic description is definitely about me specifically",
    satiricalQuote: "This horoscope is eerily accurate! It's like they know my deepest secrets and personality traits.",
    description: "Accepting vague personality descriptions as uniquely applicable to oneself. The foundation of astrology and cold reading.",
    modernExample: "Believing a personality quiz result that says 'You are outgoing but sometimes need alone time' describes you perfectly.",
    severity: "Medium",
    category: "Self-Assessment",
    algorithmicAmplification: "Very High",
    discoveredBy: "Bertram Forer (1949)",
    funFact: "People rate vague personality descriptions as highly accurate (85% accurate) when told they're personalised, even when everyone gets identical descriptions.",
    interactionCount: 1145,
    realWorldImpact: "Enables fortune telling, personality scams, and explains why generic advice often feels personally meaningful."
  },
  {
    id: 62,
    name: "Frequency Illusion",
    shortDescription: "Suddenly I see this everywhere",
    satiricalQuote: "I never noticed this car model before, but now I see it on every street. Did everyone just buy one overnight?",
    description: "Suddenly noticing something more after first learning about it. Also known as the Baader-Meinhof Phenomenon.",
    modernExample: "Learning about a rare disease and then seemingly hearing about it constantly, or buying a car and suddenly seeing that model everywhere.",
    severity: "Low",
    category: "Attention",
    algorithmicAmplification: "Very High",
    discoveredBy: "Stanford Binet (1990s)",
    funFact: "This illusion is amplified by search algorithms and social media, which show more content similar to what you've recently engaged with.",
    interactionCount: 892,
    realWorldImpact: "Creates false impressions about frequency and trends, amplified by algorithmic content curation."
  },
  {
    id: 63,
    name: "Functional Fixedness",
    shortDescription: "Objects have one job and one job only",
    satiricalQuote: "A hammer is for hammering. Using it to open a can would be ridiculous and against its fundamental nature.",
    description: "Limiting use of an object to its traditional function instead of seeing creative possibilities.",
    modernExample: "Being stuck in the rain because you won't use a newspaper as an umbrella, or refusing to use a smartphone flashlight because 'it's a phone.'",
    severity: "Medium",
    category: "Problem Solving",
    algorithmicAmplification: "Low",
    discoveredBy: "Karl Duncker (1945)",
    funFact: "Children are less affected by this bias than adults because they haven't fully learned 'proper' object functions yet.",
    interactionCount: 634,
    realWorldImpact: "Limits creative problem-solving, reduces innovation, and explains why breakthroughs often come from outsiders to a field."
  },
  {
    id: 64,
    name: "Google Effect",
    shortDescription: "Why remember when I can just Google it later?",
    satiricalQuote: "My brain is now just a search engine interface. Why waste memory space on facts?",
    description: "Forgetting information easily accessible online, also known as Digital Amnesia.",
    modernExample: "Not remembering your own phone number because it's saved in your contacts, or forgetting basic facts because 'I can just look it up.'",
    severity: "Medium",
    category: "Memory",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Betsy Sparrow (2011)",
    funFact: "People are better at remembering where to find information than the information itself, treating the internet like external memory storage.",
    interactionCount: 1456,
    realWorldImpact: "Reduces memorised knowledge, increases dependence on technology, and changes how we process and retain information."
  },
  {
    id: 65,
    name: "Groupthink",
    shortDescription: "Team harmony is more important than being right",
    satiricalQuote: "Dissent is just negativity. Let's all agree this terrible idea is brilliant for the sake of unity.",
    description: "Conforming to group consensus at the expense of critical thinking. Everyone agreeing doesn't mean anyone is right.",
    modernExample: "A startup team unanimously approving a flawed business plan because no one wants to be the 'negative' voice that questions the founder's vision.",
    severity: "High",
    category: "Group Dynamics",
    algorithmicAmplification: "High",
    discoveredBy: "Irving Janis (1972)",
    funFact: "Groupthink contributed to major disasters like the Challenger explosion and Bay of Pigs invasion - smart people making terrible collective decisions.",
    interactionCount: 1234,
    realWorldImpact: "Leads to poor organizational decisions, suppresses innovation, and explains why diverse teams often outperform homogeneous expert teams."
  },
  {
    id: 66,
    name: "Hard-Easy Effect",
    shortDescription: "I'm overconfident about hard things and under-confident about easy things",
    satiricalQuote: "I'm definitely going to nail this impossible task, but I'll probably mess up this simple one somehow.",
    description: "Overestimating chances of success on hard tasks and underestimating on easy tasks.",
    modernExample: "Feeling confident about passing a notoriously difficult exam while worrying about failing an easy online quiz.",
    severity: "Medium",
    category: "Confidence Assessment",
    algorithmicAmplification: "Low",
    discoveredBy: "Sarah Lichtenstein & Baruch Fischhoff (1977)",
    funFact: "This effect occurs because people anchor their confidence on task difficulty rather than their actual ability to complete the task.",
    interactionCount: 567,
    realWorldImpact: "Leads to poor effort allocation, inadequate preparation for easy tasks, and overconfidence in challenging situations."
  },
  {
    id: 67,
    name: "Hawthorne Effect",
    shortDescription: "I act differently when I know I'm being watched",
    satiricalQuote: "My behaviour is completely natural and authentic... now that I know you're observing and measuring it.",
    description: "Changing behaviour because one knows they are being observed. Performance improves under observation, but not for the right reasons.",
    modernExample: "Working harder when your boss is around, or eating healthier when using a diet app that tracks your food intake.",
    severity: "Medium",
    category: "Behavioural Change",
    algorithmicAmplification: "High",
    discoveredBy: "Henry Landsberger (1958)",
    funFact: "Named after the Hawthorne Works factory where productivity improved during studies, regardless of actual changes to working conditions.",
    interactionCount: 743,
    realWorldImpact: "Skews research results, creates temporary performance improvements, and explains why monitoring systems sometimes work initially but fade over time."
  },
  {
    id: 68,
    name: "Hyperbolic Discounting",
    shortDescription: "Present me deserves everything, future me can deal with consequences",
    satiricalQuote: "Why wait for a bigger reward later when I can have a smaller one right now? Future me will understand.",
    description: "Preferring smaller, immediate rewards over larger, later rewards. Patience is a virtue that present-me doesn't have.",
    modernExample: "Choosing to binge-watch Netflix instead of preparing for tomorrow's important meeting, or buying something on credit instead of saving up.",
    severity: "High",
    category: "Decision Making",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "George Ainslie (1975)",
    funFact: "This bias explains most self-control failures - people dramatically overvalue immediate rewards compared to future ones, even when the future reward is much larger.",
    interactionCount: 1367,
    realWorldImpact: "Drives procrastination, debt accumulation, health problems, and explains why immediate gratification often wins over long-term planning."
  },
  {
    id: 69,
    name: "Identifiable Victim Effect",
    shortDescription: "One specific person's suffering matters more than statistical lives",
    satiricalQuote: "A million deaths is a statistic, but one death with a name and photo is a tragedy worth donating to.",
    description: "Greater empathy for a single identifiable individual than for a large group of statistical victims.",
    modernExample: "Donating money to help one named child with medical needs while ignoring appeals to help thousands of unnamed victims of natural disasters.",
    severity: "High",
    category: "Empathy",
    algorithmicAmplification: "Very High",
    discoveredBy: "Tehila Kogut & Ilana Ritov (2005)",
    funFact: "Charities know this bias well - campaigns featuring one specific person raise more money than those showing statistics about thousands of people in need.",
    interactionCount: 889,
    realWorldImpact: "Skews charitable giving, creates unequal distribution of help, and explains why individual stories often outweigh statistical evidence in policy decisions."
  },
  {
    id: 70,
    name: "Illusion of Control",
    shortDescription: "I can influence random events with my mind",
    satiricalQuote: "If I think really hard about it and use my lucky ritual, I can totally affect this coin flip.",
    description: "Overestimating one's influence over uncontrollable events. Correlation between action and outcome means causation, obviously.",
    modernExample: "Believing you can influence slot machine results by pulling the lever in a certain way, or thinking your sports team wins because of your lucky jersey.",
    severity: "High",
    category: "Control Assessment",
    algorithmicAmplification: "Medium",
    discoveredBy: "Ellen Langer (1975)",
    funFact: "This illusion is stronger when people are personally involved in the random process, like pressing a button vs. watching someone else press it.",
    interactionCount: 1123,
    realWorldImpact: "Drives gambling addiction, creates false sense of agency, and leads to magical thinking in decision-making."
  },
  {
    id: 71,
    name: "Illusion of Transparency",
    shortDescription: "Everyone can obviously tell what I'm thinking",
    satiricalQuote: "My poker face is terrible. Everyone definitely knows I'm bluffing... wait, they can't tell? Really?",
    description: "Overestimating how well one's emotions or thoughts are understood by others.",
    modernExample: "Thinking everyone noticed your embarrassing mistake in a presentation when most people didn't even register it happened.",
    severity: "Medium",
    category: "Social Perception",
    algorithmicAmplification: "Medium",
    discoveredBy: "Thomas Gilovich, Kenneth Savitsky & Victoria Medvec (1998)",
    funFact: "People consistently overestimate how obvious their emotions are to others by about 50% - what feels glaringly obvious to you is often invisible to others.",
    interactionCount: 678,
    realWorldImpact: "Increases social anxiety, affects communication effectiveness, and explains why people often feel more exposed than they actually are."
  },
  {
    id: 72,
    name: "Impact Bias",
    shortDescription: "This will definitely change everything forever",
    satiricalQuote: "When this happens, I'll be permanently happy/devastated. My emotional state will never recover/improve.",
    description: "Overestimating the intensity or duration of emotional reactions to future events.",
    modernExample: "Thinking you'll be miserable for months if your favourite TV show gets cancelled, or believing winning the lottery will make you happy forever.",
    severity: "High",
    category: "Emotional Prediction",
    algorithmicAmplification: "Medium",
    discoveredBy: "Daniel Gilbert & Timothy Wilson (1998)",
    funFact: "People consistently overestimate both how good they'll feel about positive events and how bad they'll feel about negative ones, and how long these feelings will last.",
    interactionCount: 834,
    realWorldImpact: "Leads to poor decision-making about future commitments, overinvestment in single outcomes, and inadequate emotional preparation."
  },
  {
    id: 73,
    name: "Information Bias",
    shortDescription: "More information is always better, even when it's useless",
    satiricalQuote: "I need to research this decision for 47 more hours before acting, even though I already have enough information.",
    description: "Seeking information even when it does not affect action or improve decision-making.",
    modernExample: "Spending hours reading reviews for a $10 purchase, or demanding more data for a decision when you already have sufficient information to act.",
    severity: "Medium",
    category: "Decision Making",
    algorithmicAmplification: "Very High",
    discoveredBy: "Jonathan Baron, John Beattie & John Hershey (1988)",
    funFact: "People will pay for useless information and delay decisions to gather irrelevant data, even when told the information won't help their choice.",
    interactionCount: 756,
    realWorldImpact: "Causes analysis paralysis, delays important decisions, and explains why people often over-research trivial choices."
  },
  {
    id: 74,
    name: "Insensitivity to Sample Size",
    shortDescription: "Five people told me X, so it must be universally true",
    satiricalQuote: "I asked three friends and they all agreed, so this is basically a scientific consensus.",
    description: "Ignoring the importance of sample size in statistical reasoning. Small samples feel just as convincing as large ones.",
    modernExample: "Believing a restaurant is terrible because of three bad Yelp reviews, ignoring that it has 500 positive reviews.",
    severity: "High",
    category: "Statistical Reasoning",
    algorithmicAmplification: "High",
    discoveredBy: "Daniel Kahneman & Amos Tversky (1972)",
    funFact: "People make the same confidence judgements about patterns in samples of 10 as samples of 1000, completely ignoring statistical reliability.",
    interactionCount: 643,
    realWorldImpact: "Leads to false conclusions from limited data, explains why anecdotal evidence feels convincing, and causes poor generalisation from small samples."
  },
  {
    id: 75,
    name: "Justification of Effort",
    shortDescription: "I suffered for this, so it must be valuable",
    satiricalQuote: "The more painful it was to obtain, the more worthwhile it must be. Suffering equals value, obviously.",
    description: "Valuing outcomes more when more effort was invested, regardless of actual quality or benefit.",
    modernExample: "Thinking a difficult college course was more valuable than an easy one that taught you more useful skills, just because you struggled more.",
    severity: "Medium",
    category: "Value Assessment",
    algorithmicAmplification: "Low",
    discoveredBy: "Leon Festinger (1957)",
    funFact: "This bias explains why hazing rituals make people more loyal to groups, and why people overvalue things they worked hard to get.",
    interactionCount: 567,
    realWorldImpact: "Maintains inefficient processes, justifies unnecessary hardship, and explains why 'paying your dues' cultures persist."
  },
  {
    id: 76,
    name: "Less-is-Better Effect",
    shortDescription: "Smaller looks better when evaluated alone",
    satiricalQuote: "24 pieces of silverware sounds impressive until you see the 40-piece set right next to it.",
    description: "Preferring a lesser option when evaluated separately but not jointly. Context is everything.",
    modernExample: "Rating a 24-piece dinnerware set as excellent when seen alone, but preferring a 40-piece set when comparing them side by side.",
    severity: "Medium",
    category: "Evaluation",
    algorithmicAmplification: "Medium",
    discoveredBy: "Christopher Hsee (1998)",
    funFact: "This effect disappears when options are compared directly, showing how much evaluation context affects our preferences.",
    interactionCount: 432,
    realWorldImpact: "Affects product ratings, hiring decisions when candidates are evaluated separately, and explains why comparison shopping changes preferences."
  },
  {
    id: 77,
    name: "Misinformation Effect",
    shortDescription: "My memory is perfectly accurate, except for all the false parts I added later",
    satiricalQuote: "I remember exactly what happened, plus all the extra details I learned about it afterwards.",
    description: "Incorporating false information into memory after an event. Memory is like a Wikipedia page that anyone can edit.",
    modernExample: "Remembering details about a car accident that you learned from news reports, not from what you actually witnessed.",
    severity: "High",
    category: "Memory",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Elizabeth Loftus (1978)",
    funFact: "People can develop completely false memories of events that never happened, and these false memories feel just as real as true ones.",
    interactionCount: 1345,
    realWorldImpact: "Compromises eyewitness testimony, spreads false narratives, and explains why memories become less reliable over time."
  },
  {
    id: 78,
    name: "Naïve Realism",
    shortDescription: "I see reality objectively, everyone else is biased",
    satiricalQuote: "I'm the only one who sees things as they really are. Everyone else is blinded by their prejudices.",
    description: "Believing one sees reality objectively while others are biased. The ultimate meta-bias.",
    modernExample: "Thinking your political views are based on facts while opposing views are based on emotion and misinformation.",
    severity: "High",
    category: "Self-Assessment",
    algorithmicAmplification: "High",
    discoveredBy: "Lee Ross & Andrew Ward (1996)",
    funFact: "This bias makes people think their own biases don't exist, making it nearly impossible to recognise one's own prejudices.",
    interactionCount: 1156,
    realWorldImpact: "Prevents self-reflection, fuels political polarisation, and makes bias correction extremely difficult."
  },
  {
    id: 79,
    name: "Omission Bias",
    shortDescription: "Not doing something bad is better than doing something good that has risks",
    satiricalQuote: "I'd rather let someone die by doing nothing than risk saving them with an action that might fail.",
    description: "Judging harmful actions as worse than harmful inactions, even when outcomes are identical.",
    modernExample: "Refusing to get vaccinated (inaction) while being more accepting of risks from the disease itself (also inaction).",
    severity: "High",
    category: "Moral Reasoning",
    algorithmicAmplification: "Medium",
    discoveredBy: "Ilana Ritov & Jonathan Baron (1990)",
    funFact: "People will choose harmful inaction over beneficial action, even when the math clearly favours action.",
    interactionCount: 789,
    realWorldImpact: "Prevents beneficial interventions, maintains harmful status quos, and explains resistance to proactive solutions."
  },
  {
    id: 80,
    name: "Outgroup Derogation",
    shortDescription: "They're not just different, they're inferior",
    satiricalQuote: "It's not enough that we're good. They have to be bad for us to feel really good about ourselves.",
    description: "Viewing outgroup members negatively to enhance ingroup status. Tribalism's ugly cousin.",
    modernExample: "Not just preferring your favourite sports team, but actively believing the opposing team's fans are less intelligent or moral.",
    severity: "High",
    category: "Group Dynamics",
    algorithmicAmplification: "Very High",
    discoveredBy: "Muzafer Sherif (1966)",
    funFact: "This bias can be triggered by completely arbitrary group divisions, like being assigned to 'Team Red' vs 'Team Blue' randomly.",
    interactionCount: 1067,
    realWorldImpact: "Fuels discrimination, political hatred, and explains why group conflicts escalate beyond reasonable disagreements."
  },
  {
    id: 81,
    name: "Pareidolia",
    shortDescription: "That cloud definitely looks like Elvis",
    satiricalQuote: "The universe is constantly sending me signs through random patterns. That stain looks exactly like my grandmother!",
    description: "Perceiving meaningful patterns, like faces, in random stimuli. Pattern recognition gone wild.",
    modernExample: "Seeing faces in electrical outlets, animals in clouds, or religious figures in toast burn patterns.",
    severity: "Low",
    category: "Perception",
    algorithmicAmplification: "Medium",
    discoveredBy: "Carl Sagan (1995)",
    funFact: "This tendency helped our ancestors survive by quickly identifying faces and potential threats, but now makes us see faces everywhere.",
    interactionCount: 654,
    realWorldImpact: "Generally harmless but can contribute to superstitious thinking and conspiracy theories when people see patterns in random data."
  },
  {
    id: 82,
    name: "Pessimism Bias",
    shortDescription: "Everything will probably go wrong",
    satiricalQuote: "I'm not pessimistic, I'm realistic about how terrible everything is going to be.",
    description: "Overestimating the likelihood of negative outcomes. Murphy's Law as a cognitive bias.",
    modernExample: "Assuming your presentation will go terribly, your flight will be delayed, and your date will be a disaster.",
    severity: "Medium",
    category: "Probability Assessment",
    algorithmicAmplification: "Very High",
    discoveredBy: "Martin Seligman (1972)",
    funFact: "Mild pessimism can actually improve decision-making by encouraging better preparation, but extreme pessimism becomes self-fulfilling.",
    interactionCount: 823,
    realWorldImpact: "Reduces risk-taking, increases anxiety, but sometimes leads to better preparation and more realistic planning."
  },
  {
    id: 83,
    name: "Placebo Effect",
    shortDescription: "Believing it works makes it work",
    satiricalQuote: "This sugar pill will definitely cure me because the doctor said it would. Mind over matter!",
    description: "Experiencing real changes due to belief in treatment efficacy, even with inactive treatments.",
    modernExample: "Feeling better after taking a vitamin supplement that's actually just sugar, because you believe it's helping your immune system.",
    severity: "Medium",
    category: "Belief Effects",
    algorithmicAmplification: "High",
    discoveredBy: "Henry Beecher (1955)",
    funFact: "The placebo effect can produce measurable physiological changes, including pain relief and improved immune function.",
    interactionCount: 1234,
    realWorldImpact: "Complicates medical research, explains alternative medicine popularity, and demonstrates the power of belief on physical health."
  },
  {
    id: 84,
    name: "Post-Purchase Rationalisation",
    shortDescription: "I definitely needed this expensive thing I just bought",
    satiricalQuote: "This impulse purchase was actually a brilliant investment in my future happiness and productivity.",
    description: "Justifying purchases after the fact to reduce cognitive dissonance about spending decisions.",
    modernExample: "Convincing yourself that the expensive gadget you bought on sale was essential, even though you never use it.",
    severity: "Medium",
    category: "Consumer Behaviour",
    algorithmicAmplification: "High",
    discoveredBy: "Leon Festinger (1957)",
    funFact: "The more expensive or unnecessary the purchase, the stronger the rationalisation becomes to justify the decision.",
    interactionCount: 967,
    realWorldImpact: "Prevents learning from poor purchasing decisions, enables overconsumption, and helps maintain consumer spending habits."
  },
  {
    id: 85,
    name: "Pro-Innovation Bias",
    shortDescription: "New and shiny equals better and necessary",
    satiricalQuote: "This revolutionary technology will solve all our problems and has absolutely no downsides worth considering.",
    description: "Overvaluing new innovations and underestimating limitations or negative consequences.",
    modernExample: "Assuming every new app, cryptocurrency, or tech gadget will transform your life while ignoring potential risks or downsides.",
    severity: "Medium",
    category: "Technology Assessment",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Everett Rogers (2003)",
    funFact: "This bias explains why tech bubbles form and why people adopt new technologies before understanding their full implications.",
    interactionCount: 745,
    realWorldImpact: "Drives premature adoption of unproven technologies, creates tech bubbles, and leads to neglect of existing solutions."
  },
  {
    id: 86,
    name: "Projection Bias",
    shortDescription: "Everyone thinks exactly like I do right now",
    satiricalQuote: "Since I love spicy food today, I'll definitely love it tomorrow and everyone else probably does too.",
    description: "Assuming others share one's current feelings, beliefs, or preferences.",
    modernExample: "Ordering way too much food when hungry, assuming you'll be just as hungry later, or thinking everyone shares your current political enthusiasm.",
    severity: "High",
    category: "Social Prediction",
    algorithmicAmplification: "Medium",
    discoveredBy: "George Loewenstein, Ted O'Donoghue & Matthew Rabin (2003)",
    funFact: "This bias affects both predictions about future selves and assumptions about other people's preferences and motivations.",
    interactionCount: 834,
    realWorldImpact: "Leads to poor planning, misunderstandings in relationships, and explains why people assume others share their values."
  },
  {
    id: 87,
    name: "Restraint Bias",
    shortDescription: "I have excellent self-control, so I can handle temptation",
    satiricalQuote: "I'm so disciplined that I can keep cookies in the house and definitely won't eat them all tonight.",
    description: "Overestimating one's ability to control impulses and resist temptation.",
    modernExample: "Keeping junk food in the house because you're confident you won't overeat, then finishing the entire bag that evening.",
    severity: "High",
    category: "Self-Control",
    algorithmicAmplification: "High",
    discoveredBy: "Loran Nordgren, Joop van der Pligt & Frenk van Harreveld (2009)",
    funFact: "People who are most confident in their self-control are often the most likely to give in to temptation when it arises.",
    interactionCount: 723,
    realWorldImpact: "Leads to poor self-control strategies, explains diet failures, and contributes to addiction relapse."
  },
  {
    id: 88,
    name: "Risk Compensation",
    shortDescription: "Safety equipment makes me feel invincible",
    satiricalQuote: "I'm wearing a helmet, so I can definitely do this dangerous thing I wouldn't normally attempt.",
    description: "Adjusting behaviour in response to perceived risk changes, often negating safety improvements.",
    modernExample: "Driving more aggressively in a car with better safety features, or taking bigger risks in extreme sports when wearing protective gear.",
    severity: "Medium",
    category: "Risk Assessment",
    algorithmicAmplification: "Low",
    discoveredBy: "Gerald Wilde (1982)",
    funFact: "This bias can completely negate the benefits of safety improvements as people unconsciously increase risky behaviour.",
    interactionCount: 567,
    realWorldImpact: "Reduces effectiveness of safety measures, explains why some safety improvements don't reduce accidents as expected."
  },
  {
    id: 89,
    name: "Selective Exposure",
    shortDescription: "I only want information that confirms what I already believe",
    satiricalQuote: "Why would I read opposing viewpoints when I can just read things that prove how right I already am?",
    description: "Preferring information that supports existing views while avoiding contradictory information.",
    modernExample: "Only following news sources that align with your political views, or unfriending people on social media who post opposing opinions.",
    severity: "High",
    category: "Information Seeking",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Leon Festinger (1957)",
    funFact: "Social media algorithms amplify this bias by creating echo chambers that serve users content similar to what they've previously engaged with.",
    interactionCount: 1456,
    realWorldImpact: "Creates filter bubbles, increases polarisation, and prevents exposure to important contradictory information."
  },
  {
    id: 90,
    name: "Social Comparison Bias",
    shortDescription: "My worth depends entirely on how I stack up against others",
    satiricalQuote: "I'm not happy unless I'm doing better than my neighbours, coworkers, and random strangers on social media.",
    description: "Evaluating oneself relative to others in a biased way, often leading to distorted self-perception.",
    modernExample: "Feeling unsuccessful despite career progress because your college friends seem to be doing better based on their social media posts.",
    severity: "High",
    category: "Social Evaluation",
    algorithmicAmplification: "Extremely High",
    discoveredBy: "Leon Festinger (1954)",
    funFact: "Social media has intensified this bias because people compare their reality to others' curated highlight reels.",
    interactionCount: 1234,
    realWorldImpact: "Reduces life satisfaction, drives status competition, and explains why social media can negatively affect mental health."
  },
  {
    id: 91,
    name: "Surrogation",
    shortDescription: "This simple metric perfectly captures the complex reality",
    satiricalQuote: "Why measure actual success when we can just focus on this one number that's easy to track?",
    description: "Substituting a complex measure with a simpler proxy that becomes the goal itself.",
    modernExample: "Focusing solely on social media likes instead of meaningful engagement, or judging employee performance only by hours worked.",
    severity: "High",
    category: "Measurement",
    algorithmicAmplification: "High",
    discoveredBy: "Michael Harris & Bill Tayler (2012)",
    funFact: "This bias explains Goodhart's Law: 'When a measure becomes a target, it ceases to be a good measure.'",
    interactionCount: 645,
    realWorldImpact: "Distorts organisational behaviour, creates perverse incentives, and explains why metrics often become meaningless once they're targets."
  },
  {
    id: 92,
    name: "Time-saving Bias",
    shortDescription: "Going faster always saves proportionally more time",
    satiricalQuote: "If I drive 60 mph instead of 30 mph, I'll save twice as much time on every trip. Math is simple!",
    description: "Misestimating time saved or lost when changing speed, overvaluing small speed increases.",
    modernExample: "Thinking that driving 80 mph instead of 70 mph will save significant time on a short trip, when it only saves minutes.",
    severity: "Low",
    category: "Time Perception",
    algorithmicAmplification: "Medium",
    discoveredBy: "Ola Svenson (2008)",
    funFact: "People consistently overestimate time savings from speed increases and underestimate time costs from delays.",
    interactionCount: 423,
    realWorldImpact: "Leads to dangerous driving for minimal time savings and poor understanding of travel time relationships."
  },
  {
    id: 93,
    name: "Trait Ascription Bias",
    shortDescription: "My behaviour is situational, yours reveals your true character",
    satiricalQuote: "When I'm late, it's because of traffic. When you're late, it's because you're disorganised and disrespectful.",
    description: "Viewing one's own behaviour as situational and others' behaviour as dispositional or character-based.",
    modernExample: "Attributing your own mistakes to circumstances while assuming others' mistakes reflect their personality or competence.",
    severity: "High",
    category: "Attribution",
    algorithmicAmplification: "Medium",
    discoveredBy: "Edward Jones & Richard Nisbett (1971)",
    funFact: "This asymmetry exists because we have access to our own thoughts and circumstances but can only observe others' actions.",
    interactionCount: 892,
    realWorldImpact: "Creates unfair judgements, reduces empathy, and explains most interpersonal misunderstandings and conflicts."
  },
  {
    id: 94,
    name: "Unit Bias",
    shortDescription: "One unit equals the right amount to consume",
    satiricalQuote: "This bag of chips is obviously one serving. The package wouldn't lie to me about appropriate portions.",
    description: "Assuming a single unit of consumption is the appropriate amount, regardless of actual needs or size.",
    modernExample: "Eating an entire individual bag of chips even when it contains 2.5 servings, or drinking a whole bottle of soda because it's 'one bottle.'",
    severity: "Medium",
    category: "Consumer Behaviour",
    algorithmicAmplification: "High",
    discoveredBy: "Andrew Geier, Paul Rozin & Gheorghe Doros (2006)",
    funFact: "This bias is exploited by food companies that create larger 'individual' portions to increase consumption while maintaining the illusion of single servings.",
    interactionCount: 734,
    realWorldImpact: "Contributes to overeating, portion distortion, and explains why package sizes directly influence consumption amounts."
  },
  {
    id: 95,
    name: "Von Restorff Effect",
    shortDescription: "The weird one stands out and sticks in memory",
    satiricalQuote: "I'll definitely remember the one item on this list that's highlighted in red. The rest? Probably not.",
    description: "Remembering distinctive items better than common ones. Also known as the isolation effect.",
    modernExample: "Remembering the one person in colourful clothes at a formal event, or the single typo in an otherwise perfect document.",
    severity: "Low",
    category: "Memory",
    algorithmicAmplification: "High",
    discoveredBy: "Hedwig von Restorff (1933)",
    funFact: "This effect is used extensively in advertising and design to make brands and messages more memorable through distinctiveness.",
    interactionCount: 567,
    realWorldImpact: "Influences marketing design, explains why unusual events are more memorable, and affects learning and retention strategies."
  },
  {
    id: 96,
    name: "Well-Traveled Road Effect",
    shortDescription: "Familiar routes always seem shorter",
    satiricalQuote: "This route I take every day definitely takes less time than that unfamiliar road. I can feel it.",
    description: "Underestimating time taken on familiar routes compared to unfamiliar ones of the same length.",
    modernExample: "Thinking your daily commute is faster than an alternate route, even when GPS shows they take the same time.",
    severity: "Low",
    category: "Time Perception",
    algorithmicAmplification: "Low",
    discoveredBy: "Ola Svenson, Ulf Eriksson & Gonzalo Gonzalez (2004)",
    funFact: "Familiarity makes routes feel shorter because we pay less attention to landmarks and details, making time seem to pass faster.",
    interactionCount: 445,
    realWorldImpact: "Affects navigation choices, contributes to route preferences, and explains resistance to trying new paths."
  },
  {
    id: 97,
    name: "Zero-Sum Bias",
    shortDescription: "If you win, I must be losing",
    satiricalQuote: "Life is basically a pie, and if you get a bigger slice, that automatically makes mine smaller.",
    description: "Believing that one person's gain is another's loss in non-zero-sum situations where mutual benefit is possible.",
    modernExample: "Thinking that if immigrants succeed economically, native citizens must be worse off, ignoring that economic growth can benefit everyone.",
    severity: "High",
    category: "Resource Perception",
    algorithmicAmplification: "Very High",
    discoveredBy: "Lee Ross & Constance Stillinger (1991)",
    funFact: "This bias prevents cooperation and creates unnecessary competition even in situations where everyone could benefit from working together.",
    interactionCount: 1067,
    realWorldImpact: "Fuels political and economic conflict, prevents win-win solutions, and explains resistance to immigration and trade."
  },
  {
    id: 98,
    name: "Information Avoidance",
    shortDescription: "Ignorance is bliss, knowledge is responsibility",
    satiricalQuote: "Why learn uncomfortable truths when I can just pretend everything is fine?",
    description: "The deliberate choice to avoid or ignore information that could be useful or relevant, often to reduce cognitive dissonance or emotional discomfort.",
    modernExample: "Not opening medical test results, avoiding checking your bank balance after a spending spree, or refusing to read climate change reports.",
    severity: "High",
    category: "Information Processing",
    algorithmicAmplification: "High",
    discoveredBy: "Russell Golman & George Loewenstein (2015)",
    funFact: "People will actually pay money to avoid receiving information they think might be bad news, even when the information could help them.",
    interactionCount: 934,
    realWorldImpact: "Prevents early intervention in health and financial problems, maintains harmful ignorance, and explains why people avoid preventive care."
  },
  {
    id: 99,
    name: "Cognitive Dissonance",
    shortDescription: "My brain hurts when my beliefs contradict each other",
    satiricalQuote: "I'll just change my beliefs to match my behaviour. Much easier than changing my behaviour to match my beliefs.",
    description: "The mental discomfort experienced when holding two or more contradictory beliefs, values, or attitudes, often leading to rationalisation or attitude change to reduce the inconsistency.",
    modernExample: "Knowing smoking is unhealthy but continuing to smoke, then convincing yourself that 'everyone dies of something anyway' to reduce the mental conflict.",
    severity: "High",
    category: "Belief Consistency",
    algorithmicAmplification: "Medium",
    discoveredBy: "Leon Festinger (1957)",
    funFact: "The stronger the dissonance, the more motivated people become to resolve it - often by changing their beliefs rather than their behaviour.",
    interactionCount: 1567,
    realWorldImpact: "Drives rationalisation of harmful behaviours, explains attitude changes after commitments, and underlies most self-deception."
  },
  {
    id: 100,
    name: "Ostrich Effect",
    shortDescription: "If I can't see the problem, it doesn't exist",
    satiricalQuote: "My investment portfolio is like Schrödinger's cat - it's both fine and terrible until I check it.",
    description: "The tendency to avoid or ignore negative information or situations by 'burying one's head in the sand,' particularly in contexts like finance or health.",
    modernExample: "Not checking your investment account during market crashes, avoiding the scale when dieting isn't working, or not opening bills when money is tight.",
    severity: "High",
    category: "Avoidance Behaviour",
    algorithmicAmplification: "Medium",
    discoveredBy: "Dan Galai & Orly Sade (2006)",
    funFact: "Named after the myth that ostriches bury their heads in sand when threatened (they don't), but humans actually do this psychological equivalent regularly.",
    interactionCount: 1123,
    realWorldImpact: "Prevents early problem detection, worsens financial and health outcomes, and explains why people avoid monitoring important metrics."
  }
];

export const biasQuizzes = [
  {
    id: 1,
    title: "The Social Media Bias Scanner",
    description: "Upload a screenshot of your feed and discover what biases are being served to you",
    type: "interactive",
    questions: [
      {
        scenario: "You see a news article that makes you angry. What's your first instinct?",
        options: [
          "Share it immediately to warn others",
          "Check the source and date",
          "Look for counterarguments",
          "Scroll past because you don't want negativity"
        ],
        biasRevealed: "Confirmation Bias & Aesthetic Bias"
      }
    ]
  },
  {
    id: 2,
    title: "The Algorithm Resistance Test",
    description: "See how well you can resist your personalised content bubble",
    type: "challenge",
    duration: "5 minutes"
  }
];

export const userStats = {
  biasesDiscovered: 12,
  streakDays: 7,
  totalInteractions: 156,
  favouriteCategory: "Information Processing",
  resistanceScore: 73
};

export const dailyReflections = [];

export const satiricalInsights = [
  "Congratulations! Your brain just demonstrated the same reasoning that kept us in trees for millions of years.",
  "Plot twist: The algorithm knows you better than you know yourself. Uncomfortable yet?",
  "Fun fact: You just fell for a bias discovered by psychologists who were probably biased themselves.",
  "Breaking news: Local human discovers they're not as rational as they thought. Water is wet.",
  "Achievement unlocked: You've successfully demonstrated why democracy is terrifying.",
  "Your confidence in your own rationality is inversely proportional to your actual rationality. Ironic, isn't it?",
  "The human brain: 3 billion years of evolution perfecting the art of being wrong with complete certainty.",
  "Warning: Your thoughts may be more influenced by what you had for breakfast than you realise.",
  "Good news: You're not as unique as you think. Bad news: You're not as rational as you think either.",
  "Evolution gave you pattern recognition. Marketing gave you patterns to recognize. Guess who's winning?",
  "Your memory is basically Wikipedia if Wikipedia could edit itself while you weren't looking.",
  "Statistically speaking, you're probably wrong about statistics. And about speaking, for that matter.",
  "The only thing more predictable than human irrationality is humans' surprise at being irrational.",
  "Your brain runs on the same logic that makes people argue about pineapple on pizza. Let that sink in.",
  "Reminder: Every decision you make is influenced by biases you don't know you have. Sweet dreams!"
];